import { z } from 'zod';
/**
 * Horaires d'ouverture et rendez-vous.
 *
 * ⭐ Les horaires vivent dans les RÉGLAGES de la boutique, pas dans le code :
 * un salon ouvre le samedi et ferme le lundi, un cabinet fait l'inverse, et
 * aucun des deux ne doit attendre une mise à jour de l'application pour le
 * dire.
 */
/** Une heure de la journée, en horloge murale : « 09:00 », « 18:30 ». */
export const heureSchema = z
    .string()
    .regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Heure attendue au format HH:MM');
/**
 * Une plage d'ouverture.
 *
 * ⚠️ Comparaison de CHAÎNES pour ordonner deux heures : « 09:00 » < « 18:30 »
 * lexicographiquement parce que le format est à largeur fixe et zéro-préfixé.
 * C'est exact, et cela évite de fabriquer deux objets Date — donc d'inviter un
 * fuseau horaire dans une question qui n'en a pas.
 */
export const plageSchema = z
    .object({ start: heureSchema, end: heureSchema })
    .refine((p) => p.start < p.end, {
    message: 'La fin doit suivre le début',
    path: ['end'],
});
/**
 * Une journée type.
 *
 * ⭐ PLUSIEURS plages par jour, et non une seule amplitude : la pause de midi
 * est la règle, pas l'exception. Sans elle, on proposerait des rendez-vous à
 * 13 h dans une boutique fermée, et le commerçant n'aurait d'autre recours que
 * de refuser le client au moment où il se présente.
 */
export const journeeSchema = z.object({
    /** Aucune plage suffit à dire « fermé » — mais le dire explicitement se lit mieux. */
    closed: z.boolean().default(false),
    ranges: z.array(plageSchema).max(4, 'Au plus quatre plages dans une journée').default([]),
});
/**
 * Les horaires de la semaine.
 *
 * ⚠️ SEPT entrées, indexées comme `Date#getDay` — 0 = dimanche, 6 = samedi.
 * Suivre la convention de la plateforme plutôt qu'un « lundi d'abord » plus
 * naturel en français évite une conversion à chaque lecture de date : c'est
 * exactement le genre de décalage d'un cran qui ne se voit qu'un dimanche.
 * L'AFFICHAGE, lui, commence le lundi — c'est l'affaire de l'écran.
 */
export const horairesSchema = z.object({
    days: z.array(journeeSchema).length(7, 'Sept journées attendues'),
    /**
     * Le pas de la grille, en minutes.
     *
     * ⚠️ À ne pas confondre avec la DURÉE d'une prestation : le pas dit tous les
     * combien un rendez-vous peut commencer (toutes les 15 min), la durée dit
     * combien de temps il occupe (45 min). Une manucure de 45 min proposée sur
     * un pas de 15 peut donc commencer à 9 h 00, 9 h 15 ou 9 h 30.
     */
    slotMinutes: z.coerce.number().int().min(5).max(240).default(30),
});
/** Lundi au vendredi, 9 h – 12 h 30 puis 14 h – 18 h. Un défaut, pas une norme. */
export function horairesParDefaut() {
    const ouvre = {
        closed: false,
        ranges: [
            { start: '09:00', end: '12:30' },
            { start: '14:00', end: '18:00' },
        ],
    };
    const ferme = { closed: true, ranges: [] };
    // Index 0 = dimanche.
    return { days: [ferme, ouvre, ouvre, ouvre, ouvre, ouvre, ferme], slotMinutes: 30 };
}
export const JOURS_FR = [
    'Dimanche',
    'Lundi',
    'Mardi',
    'Mercredi',
    'Jeudi',
    'Vendredi',
    'Samedi',
];
// ─────────────────────────────────────────────────────────── les rendez-vous
/**
 * ⭐ `pending` : pris EN LIGNE, pas encore confirmé par la boutique.
 *
 * Un créneau convenu au comptoir est ferme ; un créneau posé depuis le site
 * l'a été par quelqu'un qu'on n'a pas eu au téléphone. Le distinguer permet à
 * la boutique de valider — et il occupe la place entre-temps, sinon deux
 * visiteurs réserveraient le même fauteuil en attendant chacun sa réponse.
 */
export const statutRdvSchema = z.enum(['pending', 'booked', 'done', 'cancelled', 'no_show']);
export const LIBELLES_STATUT_RDV = {
    pending: 'À valider',
    booked: 'Réservé',
    done: 'Honoré',
    cancelled: 'Annulé',
    no_show: 'Absent',
};
/**
 * Ce qu'on envoie pour poser un rendez-vous.
 *
 * ⚠️ Les bornes sont des INSTANTS (ISO 8601 avec fuseau), pas des heures
 * murales : deux postes de caisse peuvent être réglés sur des fuseaux
 * différents, et c'est la base qui arbitre les chevauchements. La conversion
 * « jeudi 14 h dans la boutique » → instant se fait au moment de la saisie,
 * une seule fois, là où le fuseau de la boutique est connu.
 */
/**
 * Un identifiant facultatif.
 *
 * ⚠️ `null` est ACCEPTÉ, pas seulement l'absence. Un appareil qui relit un
 * rendez-vous dans sa réplique y trouve des `null` — c'est ainsi qu'une
 * colonne vide traverse — et les renvoie tels quels en modifiant le créneau.
 * Un schéma qui n'admet que `string | undefined` rejetait alors la mutation
 * avec « Expected string, received null », et le déplacement partait en
 * « non envoyé » sans que l'écran sache pourquoi.
 */
const idFacultatif = z
    .string()
    .uuid()
    .or(z.literal(''))
    .nullish()
    .transform((v) => v || undefined);
export const rendezVousInputSchema = z
    .object({
    id: z.string().uuid().optional(),
    customerId: z.string().uuid(),
    itemId: idFacultatif,
    /** La vente, le devis ou la commande qui engage le créneau. */
    transactionId: idFacultatif,
    /**
     * La PLACE réservée, dans la catégorie de services de la prestation.
     *
     * ⚠️ C'est le grain de la disponibilité : la base refuse deux rendez-vous
     * qui se chevauchent SUR LE MÊME POSTE, et les laisse passer sur deux
     * postes différents. Facultatif pour les rendez-vous antérieurs aux postes,
     * qui gardent l'ancienne règle — un seul à la fois.
     */
    posteId: idFacultatif,
    /** Copié de l'article : l'agenda passé ne se réécrit pas au renommage. */
    label: z.string().trim().min(1, 'Libellé requis').max(160),
    startsAt: z.string().datetime({ offset: true }),
    endsAt: z.string().datetime({ offset: true }),
    status: statutRdvSchema.default('booked'),
    notes: z
        .string()
        .trim()
        .max(2000)
        .nullish()
        .transform((v) => v || undefined),
})
    .refine((v) => v.endsAt > v.startsAt, {
    message: 'La fin doit suivre le début',
    path: ['endsAt'],
});
/** Filtres de l'agenda : une fenêtre de temps, jamais « tout ». */
export const rendezVousQuerySchema = z.object({
    /** Borne basse INCLUSE, en ISO. */
    from: z.string().datetime({ offset: true }),
    /** Borne haute EXCLUE. */
    to: z.string().datetime({ offset: true }),
    customerId: z.string().uuid().optional(),
    /** Par défaut, les annulés sont écartés : un créneau libéré n'encombre pas. */
    includeCancelled: z.coerce.boolean().default(false),
});
/**
 * Un rendez-vous occupe-t-il encore son créneau ?
 *
 * ⚠️ Un rendez-vous ANNULÉ libère l'horaire ; un « absent » ne le libère pas
 * rétroactivement — l'heure a bel et bien été perdue, et la faire réapparaître
 * comme disponible dans le passé donnerait un agenda qui ment sur ce qui s'est
 * réellement passé.
 */
export function occupeLeCreneau(statut) {
    return statut !== 'cancelled';
}
// ------------------------------------------------------ blocages de réservation
/**
 * Fermer des POSTES d'une catégorie sur un intervalle.
 *
 * ⭐ Le besoin réel : une formation mobilise deux fauteuils sur trois pendant
 * une semaine. La boutique continue de prendre des rendez-vous sur le
 * troisième — ce qu'un « jour fermé » ne sait pas exprimer. On réduit donc la
 * CAPACITÉ, on n'interdit pas des heures.
 *
 * ⚠️ Le blocage dit COMBIEN de postes ferment, jamais LESQUELS. Nommer les
 * postes obligerait le gérant à choisir entre des fauteuils interchangeables,
 * et à réaffecter ses rendez-vous pour libérer celui qu'il vient de bloquer.
 */
export const blocageInputSchema = z
    .object({
    categoryId: z.string().uuid(),
    debut: z.string().datetime({ offset: true }),
    /** Borne haute EXCLUE, comme partout ailleurs dans l'agenda. */
    fin: z.string().datetime({ offset: true }),
    postes: z.coerce.number().int().min(1).max(100),
    motif: z.string().trim().max(200).optional(),
})
    .refine((v) => v.fin > v.debut, {
    message: 'La fin doit suivre le début',
    path: ['fin'],
});
//# sourceMappingURL=agenda.js.map